// ─── Compact Vinyl Player ───────────────────────────────────────
// Phase 1.9.17 — single shared visual for the free-form canvas music
// widget. Used by VinylElement in BOTH Studio and public so the two
// can never visually drift. Fully contained inside its parent wrapper
// (the canvas element saved bounds in CollagePublicCanvas / Studio).
//
// • interactive=false (Studio):
//     The play button is rendered but stops propagation so it doesn't
//     deselect the element. Designers can still preview audio if they
//     intentionally click the small play affordance.
// • interactive=true  (Public):
//     The same play button toggles real playback. Vinyl spins while
//     playing. No "Now Playing" chrome, no scrubber, no floating
//     mini-player — those belonged to the legacy hero template.
//
// Layout primitives match the editor preview that previously lived in
// VinylElement.js so existing saved widgets render identically.

import React, { useRef, useState, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { useInvitationAudio } from '../../audio/InvitationAudioContext';

function lightenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + amount);
  const g = Math.min(255, ((num >> 8) & 0xff) + amount);
  const b = Math.min(255, (num & 0xff) + amount);
  return `rgb(${r},${g},${b})`;
}

function darkenColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, ((num >> 16) & 0xff) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `rgb(${r},${g},${b})`;
}

// Local CSS injected once so we don't depend on global stylesheets.
let _injected = false;
function injectStyles() {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const style = document.createElement('style');
  style.setAttribute('data-cvp', '1');
  style.textContent = `
    @keyframes cvp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .cvp-disc-spin { animation: cvp-spin 4s linear infinite; transform-origin: 50% 50%; }
  `;
  document.head.appendChild(style);
}

export default function CompactVinylPlayer({
  audioSrc = '',
  songTitle = 'Our Song',
  artistName = 'Wedding Soundtrack',
  coverImage = '',
  accentColor = '#2c241d',
  bgColor = '#f8f3eb',
  discColor = '#000000',
  labelColor = '#efe5d6',
  tonearmX = 50,
  tonearmY = -12,
  tonearmScale = 125,
  tonearmColor,
  interactive = false,
  testId,
}) {
  useEffect(() => { injectStyles(); }, []);

  // Phase 1.9.18 — when an InvitationAudioProvider is in the tree we
  // consume its shared audio/state instead of creating a local <audio>.
  // This keeps the opener tap-gate and the vinyl in lock-step: a single
  // track, a single playing state. When no provider exists (e.g. Studio
  // preview, or any standalone usage) we fall back to a local <audio>
  // so previous behavior is preserved.
  const sharedAudio = useInvitationAudio();
  const isShared = !!sharedAudio && !!sharedAudio.hasSource;

  const localAudioRef = useRef(null);
  const [localPlaying, setLocalPlaying] = useState(false);

  // Local-audio event listeners — only used when no provider is present.
  useEffect(() => {
    if (isShared) return undefined;
    const a = localAudioRef.current;
    if (!a) return undefined;
    const onPlay = () => setLocalPlaying(true);
    const onPause = () => setLocalPlaying(false);
    const onEnded = () => setLocalPlaying(false);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      try { a.pause(); } catch (_) { /* noop */ }
    };
  }, [isShared]);

  const playing = isShared ? !!sharedAudio.playing : localPlaying;

  const togglePlay = (e) => {
    e.stopPropagation();
    if (isShared) { sharedAudio.toggle(); return; }
    const audio = localAudioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => { /* autoplay blocked etc. */ });
    else audio.pause();
  };

  const resolvedTonearm = tonearmColor || accentColor;
  const discGradient = discColor === '#000000'
    ? 'radial-gradient(circle at center, #2f2f2f 0%, #171717 50%, #0d0d0d 70%, #000 100%)'
    : `radial-gradient(circle at center, ${lightenColor(discColor, 30)} 0%, ${discColor} 50%, ${darkenColor(discColor, 20)} 70%, ${darkenColor(discColor, 40)} 100%)`;

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center py-4 px-3"
      style={{ backgroundColor: bgColor }}
      data-testid={testId}
    >
      {/* Disc + Tonearm — tonearm overlaps disc at top-right.
          Phase 1.9.18 — in shared-audio mode the disc itself becomes the
          tap target (the standalone play button below is hidden).
          In standalone mode the disc is purely decorative and the small
          play button below controls audio.
          Phase 1.9.17 — Studio click on the widget body still selects the
          element because we stopPropagation on both onMouseDown and click. */}
      <div
        className="relative w-[85%]"
        style={{ aspectRatio: '1.2 / 1', cursor: isShared ? 'pointer' : 'default' }}
        onClick={isShared ? togglePlay : undefined}
        onMouseDown={isShared ? (e) => e.stopPropagation() : undefined}
        role={isShared ? 'button' : undefined}
        aria-label={isShared ? (playing ? 'Pause music' : 'Play music') : undefined}
        data-testid={isShared ? 'cvp-disc-tap-target' : undefined}
      >
        {/* Disc — 80% width, bottom-left aligned. Spins when playing. */}
        <div
          className={`absolute ${playing ? 'cvp-disc-spin' : ''}`}
          style={{ left: '0%', bottom: '0%', width: '80%', paddingBottom: '80%' }}
        >
          <div className="absolute inset-[8%] rounded-full bg-black/15 blur-xl" />
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: discGradient,
              boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
              border: '2px solid rgba(255,255,255,0.15)',
            }}
          >
            {[10, 18, 26, 34].map((inset) => (
              <div key={inset} className="absolute rounded-full border border-white/[0.08]" style={{ inset: `${inset}%` }} />
            ))}
            <div className="absolute rounded-full" style={{ inset: '6%', background: 'conic-gradient(from 210deg, rgba(255,255,255,0.12), transparent 25%, transparent 75%, rgba(255,255,255,0.07))' }} />
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/20 overflow-hidden"
              style={{ width: '52%', height: '52%', backgroundColor: labelColor }}
            >
              {coverImage ? <img src={coverImage} alt="" className="w-full h-full object-cover" draggable={false} /> : null}
            </div>
            <div className="absolute left-1/2 top-1/2 h-[5%] w-[5%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#111] border border-white/30 z-10" />
          </div>
        </div>

        {/* Tonearm — stationary; sits above the (spinning) disc */}
        <div
          className="absolute z-30"
          style={{
            right: `${50 - tonearmX}%`,
            top: `${tonearmY}%`,
            width: `${38 * tonearmScale / 100}%`,
            height: `${62 * tonearmScale / 100}%`,
            transformOrigin: '75% 8%',
          }}
        >
          <TonearmSVG accentColor={resolvedTonearm} />
        </div>
      </div>

      {/* Song info */}
      <div className="mt-3 text-center w-full">
        <p className="text-[11px] font-semibold truncate" style={{ color: accentColor }}>{songTitle}</p>
        <p className="text-[9px] opacity-50 truncate" style={{ color: accentColor }}>{artistName}</p>
      </div>

      {/* Audio + standalone play button — only rendered when we are
          NOT consuming a shared invitation audio context (i.e. Studio
          preview or any other standalone usage). In shared mode the
          disc itself is the tap target and the audio element is owned
          by InvitationAudioProvider. */}
      {!isShared && audioSrc && (
        <div className="mt-2 flex justify-center">
          <audio ref={localAudioRef} src={audioSrc} preload="metadata" />
          <button
            type="button"
            onClick={togglePlay}
            onMouseDown={(e) => e.stopPropagation()}
            className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: accentColor, pointerEvents: 'auto' }}
            aria-label={playing ? 'Pause' : 'Play'}
            data-testid={interactive ? 'cvp-play-btn-public' : 'cvp-play-btn-studio'}
          >
            {playing
              ? <Pause className="w-3 h-3" style={{ color: bgColor }} />
              : <Play  className="w-3 h-3 ml-0.5" style={{ color: bgColor }} />
            }
          </button>
        </div>
      )}
    </div>
  );
}

// Tonearm SVG — same paths as the previous in-place editor preview.
function TonearmSVG({ accentColor = '#2b2c2e' }) {
  const main = accentColor;
  const detail = lightenColor(accentColor, 120);
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="395 295 100 285" className="w-full h-full" style={{ objectFit: 'contain' }}>
      <path fill={main} stroke={main} strokeMiterlimit="10" d="M452.7,363.8h.7c3.2,1,5.8.4,8.9-.6-.9,0-1.8,0-2.7,0,.4-5.5,2.4-10.4,3.8-15.8-3.1-.3-6-1.5-9.1-1.8-1,5-2.6,9.9-3.3,15-.3-3.7.3-7.3,1.2-10.9-1-1.4-1-2.5-1.1-4.2-3.3-2.7-7.1-2.8-10.9-4.4-2.3,3.1-1.8,7.9-1.1,11.5,1.2,3.4,2.8,7.6,6.2,9.3,2.6.4,4.9,1.7,7.5,1.8ZM459.1,341.3c1.2,0,2.5,0,3.7-.1,1.2-2.9,1.6-4.8,1.3-7.9-1.7.2-4.2-1-4.9,1.1-.2,2.3,0,4.6,0,6.9M464.4,312.1c4.3.7,8.7,1.4,12.4,3.8.3,6-2.8,12-3.4,17.9.3.8.7,1.4,1.2,2,7.6,7.5,8.7,21.1,1.7,29.3-1.8,1.8-3.2,3.1-3.4,5.8-.8,1-1.5,2-2.2,3.1,3,1.4,4-.4,6.1-2.1.5.9,1,1.9,1.5,2.8-1.4,1.8-4.4,4.5-4.2,7,.8,2.8,2.3,5.5,3.6,8.1,1.4,2.8,4.6,5.2,4.4,8.6-.8,1.2-2.6,1.2-3.8.8-2.8-2.8-4.5-7-6.3-10.5-1.3-2.5-1.3-5.7-4-7.1-1.8-.6-3.3-1.4-5.2-1l.6-2.1c-1.2,0-1.9-.5-2.8-1.2,0,2.5-.1,5,.1,7.4,2,15.9,5.2,31.7,9.5,47.1,2.3,12.7,5.6,25.1,7.5,37.9,1.8,10.2,1.8,20.8.2,31.1-1.7,9.3-5.7,17.5-10.5,25.6-3,3.8-5.3,8.1-8.2,11.9-1.4,2-2,4.3-3.6,6.2-3.6,4.6-5.9,9.9-9.7,14.4-1.7,1.9-2.4,3.7-2.9,6.2,1,1.1,2.1,2,3.3,2.8-.5.6-1.1,1.2-1.6,1.8-1.6-1.2-2.9-2.4-4.9-2.6,1.2,1.8,4,2.6,5.8,3.4.8-.4,1.5-.8,2.3-1.2.7,1.1,1.3,2.3,1.8,3.4-4.1.2-8.2-1.8-12.2-1.3-3.9,2.3-7,6.9-10.1,10.2-1.3,1.5-3,.7-4.5-.2-5-3.1-9.7-6.6-13.7-11,1.2-.2,2.2-.7,3.4-.1,3.1,1.9,5.9,4.9,9.3,6.1-3.3-1.8-6.4-4.2-9.3-6.6-1.7-.4-2.6-.4-3.4-2.1,5.7-8.2,12.1-16,18.6-23.6,2.1-3,6.7-2.8,9.4-5.6,2.7-2.5,2.8-5.6,4.5-8.5,6.4-3.2,11.1-9.6,15.3-15.3-.5,2.4-2.5,3.6-3.9,5.8-2.5,3.4-4.6,7.8-8.7,9.3,1,1.6,3.5,2.2,5.1,3.1,2.1-2.6,4.4-5,6.7-7.4,3.1-3.3,4.5-7.3,8-10.3-1.6,5.7-5.5,9.7-9.3,14.1-2,2-3.6,4.4-5.8,6.2,1.3-.3,3,0,3.9-1.1,5.5-5.4,11.7-11.8,13.8-19.5,1.4-4.8,4.6-9.6,5-14.6,0-3,1.2-5.9,1.4-8.9.5-7.7,0-15.3-.7-23-2.1-13-5.6-25.8-8.9-38.5-1.8-10.1-4.7-20.6-5-30.9-1.2-2.5-.6-4.9-1.1-7.4-2.6-5.7-1.5-12.9-1.7-19.1-1.6-.5-3.2-.5-4.8,0-1.3,5.2.2,11.2.5,16.5,1.2,7,1.6,14.1,3.2,21,3.9,21.4,10.3,41.1,12.4,63-.3,4.8,1.4,9.2.9,14-1,6.6-.9,12.7-3.9,18.8-1.3,2.4-2,5-4.6,6.3,2-2.8,3.4-6.1,4.9-9.2,3.1-11.3,3.2-22.9,1.1-34.5-3.1-17-7.2-33.6-10.6-50.5-2.4-10.3-3.5-20.4-4.8-31-.9-4.7-.3-9.4-.8-14.1-2-2.7-5.5-4.3-7.4-7.2-3.2-4.3-4.4-9.3-5.2-14.5-.8-7.2,2.5-15.2,9.2-18.5,2.3-1.5,5-1.5,7.2-2.9,2.3-1.9,4.5-1,7.1-.5,1.8.5,3.5.8,5.4.9.3-1,.7-1.9,1.1-2.8.6-3.6,1.7-7.2,3.4-10.4-1.2-1.2-2.6-1.3-4.2-1.7-2.1-1.1-3-.3-4.8.7-.3.8,0,3.3-1.5,2.9-.3-.5-.6-1-1-1.5-1.1,2.4,0,7.6-3.6,7.9v-2.1c.9-3.1,2.4-5.9,3.3-8.9.7-2.2,4.2-1.8,5.9-1.7"/>
      <path fill={detail} d="M466,314.9c-.9,2.3-1.3,4.1-1.1,6.6-.6,2-1.3,3.9-1.9,5.9-.9.5-1.8,1-2.8,1.5-2.6-.5-4.8-1.4-7.1.5.4-2.3,1.2-4.5,2-6.6v2.1c3.5-.3,2.4-5.4,3.5-7.9.3.5.6,1,1,1.5,1.4.4,1.2-2,1.5-2.9,1.8-1,2.7-1.8,4.8-.7Z"/>
      <path fill="#fff" d="M466,314.9c1.6.4,3,.5,4.2,1.7-1.7,3.2-2.8,6.8-3.4,10.4-.4.9-.8,1.9-1.1,2.8-1.9-.1-3.6-.4-5.4-.9.9-.5,1.8-1,2.8-1.5.6-2,1.3-3.9,1.9-5.9-.3-2.5.1-4.3,1.1-6.6Z"/>
      <path fill={detail} d="M464,333.3c.3,3.2,0,5-1.3,7.9-1.2,0-2.4.1-3.7.1,0-2.3-.2-4.6,0-6.9.7-2.1,3.2-.9,4.9-1.1Z"/>
      <path fill={detail} d="M440.1,341.2c3.8,1.6,7.6,1.7,10.9,4.4,0,1.7,0,2.8,1.1,4.2-.9,3.6-1.4,7.2-1.2,10.9.7-5.1,2.3-10,3.3-15,3.1.3,6,1.5,9.1,1.8-1.3,5.4-3.4,10.3-3.8,15.8.9,0,1.8,0,2.7,0-3.1.9-5.7,1.6-8.9.7,1,0,2-.1,2.9-.4,0-.8.1-1.6,0-2.4-1.2.8-3.2,1.2-3.6,2.6-2.6-.2-5-1.4-7.5-1.8-3.4-1.7-5-5.9-6.2-9.3-.8-3.6-1.2-8.4,1.1-11.5Z"/>
      <path fill={detail} d="M476.2,365.1c.3,3.2,1.3,5.1,3.1,7.7-.4.6-.8,1.3-1.2,1.9-.5-.9-1-1.9-1.5-2.8-2.1,1.7-3.1,3.5-6.1,2.1.7-1.1,1.5-2.1,2.2-3.1.2-2.7,1.6-4.1,3.4-5.8Z"/>
      <path fill={detail} d="M451,372.3c1.6-.4,3.2-.4,4.8,0,.2,6.3-.9,13.4,1.7,19.1.5,2.5,0,5,1.1,7.4.3,10.3,3.2,20.7,5,30.9,3.2,12.7,6.8,25.5,8.9,38.5-2.7,1.7-1.1,6.8-1.3,9.5,2.1,7.5-.1,13.7-1.2,21l1.8,1.4c-.5,5-3.6,9.9-5,14.6-2.1,7.6-8.2,14.1-13.8,19.5-.9,1.1-2.6.9-3.9,1.1,2.2-1.8,3.8-4.2,5.8-6.2,3.8-4.4,7.7-8.4,9.3-14.1-3.5,2.9-5,7-8,10.3-2.3,2.4-4.6,4.9-6.7,7.4-1.6-.9-4.1-1.5-5.1-3.1,4.1-1.5,6.2-5.9,8.7-9.3,1.4-2.2,3.4-3.4,3.9-5.8.6-1.1,1.4-2,2.4-2.8,2.6-1.3,3.3-3.9,4.6-6.3,3-6.1,2.9-12.2,3.9-18.8.6-4.8-1.2-9.2-.9-14-2-21.9-8.5-41.6-12.4-63-1.7-6.9-2.1-14-3.2-21-.3-5.3-1.8-11.3-.5-16.5Z"/>
      <path fill="#fff" d="M460.4,377.3c.9.6,1.6,1.2,2.8,1.2l-.6,2.1c2-.4,3.5.4,5.2,1-2.3.2-4.7.3-7,.4-.1.9-.2,1.8-.3,2.8-.2-2.5,0-5-.1-7.4Z"/>
      <path fill="#fff" d="M472.6,468.3c.8,7.7,1.2,15.3.7,23-.2,3-1.3,5.9-1.4,8.9l-1.8-1.4c1-7.3,3.3-13.6,1.2-21,.3-2.8-1.3-7.8,1.3-9.5Z"/>
    </svg>
  );
}
